import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { tableauDeBord, messageErreur } from '../services/api';
import { STATUTS_PROJET, STATUTS_TACHE, libelle } from '../constants';
import { Avancement, BadgePriorite, Chargement, Erreur, Vide } from '../components/communs';
import { dateCourte, enRetard, ilYA, nomComplet } from '../utils/format';

function Tuile({ libelle: l, valeur, detail, alerte }) {
  return (
    <div className="tuile">
      <span className="tuile-label">{l}</span>
      <span className={`tuile-valeur ${alerte && valeur > 0 ? 'alerte' : ''}`}>{valeur}</span>
      {detail && <span className="tuile-detail">{detail}</span>}
    </div>
  );
}

// Barres horizontales (gabarit « rang » du Hub).
function Rang({ lignes, valeur, couleur = 'var(--serie-1)', vide }) {
  if (!lignes.length) return <div className="faible" style={{ fontSize: 13 }}>{vide}</div>;
  const max = Math.max(...lignes.map(valeur), 1);
  return (
    <div className="rang">
      {lignes.map((l, i) => (
        <div className="rang-ligne" key={i} style={{ gridTemplateColumns: 'minmax(120px,1.4fr) 3fr auto' }}>
          <div className="rang-nom"><span title={l.libelle}>{l.libelle}</span></div>
          <div className="rang-piste"><div className="rang-barre" style={{ width: `${(100 * valeur(l)) / max}%`, background: couleur }} /></div>
          <div className="rang-valeur">{valeur(l)}{l.detail && <span className="faible" style={{ fontWeight: 400 }}> {l.detail}</span>}</div>
        </div>
      ))}
    </div>
  );
}

export default function TableauDeBord({ moi }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  // Un administrateur arrive sur SES projets ; « Tout le groupe » élargit.
  // Le choix est dans l'adresse : le retour arrière le conserve.
  const [params, setParams] = useSearchParams();
  const admin = moi.role === 'admin';
  const voirGroupe = admin && params.get('perimetre') === 'groupe';

  const charger = useCallback(() => {
    setErreur(null);
    setD(null);
    tableauDeBord(voirGroupe ? 'groupe' : undefined).then(setD).catch((e) => setErreur(messageErreur(e)));
  }, [voirGroupe]);
  useEffect(() => { charger(); }, [charger]);

  const changerPerimetre = (groupe) => setParams(groupe ? { perimetre: 'groupe' } : {}, { replace: true });
  const groupe = d?.perimetre === 'groupe';
  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Tableau de bord</h1>
          <p>{voirGroupe
            ? 'Vue du groupe : tous les projets et toutes les tâches.'
            : 'Vos projets et vos tâches.'}</p>
        </div>
        <div className="page-header-actions">
          {admin && (
            <div className="segmente" role="group" aria-label="Périmètre du tableau de bord">
              <button type="button" className={voirGroupe ? '' : 'actif'} aria-pressed={!voirGroupe} onClick={() => changerPerimetre(false)}>Mes projets</button>
              <button type="button" className={voirGroupe ? 'actif' : ''} aria-pressed={voirGroupe} onClick={() => changerPerimetre(true)}>Tout le groupe</button>
            </div>
          )}
          <Link className="btn" to="/mes-taches">Mes tâches</Link>
          <Link className="btn btn-primaire" to="/projets?nouveau=1">Nouveau projet</Link>
        </div>
      </div>

      {erreur && <Erreur onReessayer={charger}>{erreur}</Erreur>}
      {!d && !erreur && <Chargement />}
      {d && (
        <>
          <div className="tuiles">
            <Tuile libelle="Projets actifs" valeur={d.kpi.projets_actifs} detail={groupe ? 'dans le groupe' : 'où vous intervenez'} />
            <Tuile libelle="Tâches ouvertes" valeur={d.kpi.taches_ouvertes} detail={`dont ${d.kpi.mes_ouvertes} pour vous`} />
            <Tuile libelle="En retard" valeur={d.kpi.taches_en_retard} detail="échéance dépassée" alerte />
            <Tuile libelle="Priorité haute" valeur={d.kpi.taches_haute} detail="tâches ouvertes" />
            <Tuile libelle="Terminées en 7 jours" valeur={d.kpi.terminees_7j} />
          </div>

          <div className="grille-2" style={{ marginBottom: 'var(--esp-4)' }}>
            <section className="carte">
              <h2 className="carte-titre">Mes tâches à traiter</h2>
              {d.mes_taches.length === 0 ? <Vide>Aucune tâche ouverte ne vous est assignée.</Vide> : (
                <div className="liste-simple">
                  {d.mes_taches.map((t) => (
                    <Link key={t.id} className="liste-ligne" to={`/projets/${t.projet_id}?tache=${t.id}`}>
                      <BadgePriorite priorite={t.priorite} />
                      <span className="liste-titre">{t.titre}<div className="liste-sous">{t.projet_nom} · {libelle(STATUTS_TACHE, t.statut)}</div></span>
                      {t.date_echeance && <span className={enRetard(t.date_echeance, t.statut) ? 'retard' : 'doux'} style={{ fontSize: 12 }}>{dateCourte(t.date_echeance)}</span>}
                    </Link>
                  ))}
                </div>
              )}
            </section>
            <section className="carte">
              <h2 className="carte-titre">Projets en cours</h2>
              {d.projets.length === 0 ? <Vide>Aucun projet en cours. <Link to="/projets?nouveau=1">Créer un projet</Link></Vide> : (
                <div className="liste-simple">
                  {d.projets.map((p) => (
                    <Link key={p.id} className="liste-ligne" to={`/projets/${p.id}`} style={{ flexWrap: 'wrap' }}>
                      <span className="liste-titre">{p.nom}
                        <div className="liste-sous">
                          {libelle(STATUTS_PROJET, p.statut)}
                          {p.entreprise_nom && ` · ${p.entreprise_nom}`}
                          {p.responsable_prenom && ` · ${nomComplet(p, 'responsable_')}`}
                          {p.date_echeance && <> · <span className={enRetard(p.date_echeance, p.statut) ? 'retard' : ''}>échéance {dateCourte(p.date_echeance)}</span></>}
                        </div>
                      </span>
                      <div style={{ width: 150 }}><Avancement fait={p.nb_terminees} total={p.nb_taches} /></div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="grille-2" style={{ marginBottom: 'var(--esp-4)' }}>
            <section className="carte">
              <h2 className="carte-titre">{groupe ? 'Charge par personne' : 'Charge sur vos projets'}</h2>
              <Rang
                lignes={d.charge.map((c) => ({
                  libelle: nomComplet(c) || `Profil n° ${c.id}`,
                  n: c.ouvertes,
                  detail: [c.haute && `${c.haute} haute`, c.en_retard && `${c.en_retard} en retard`].filter(Boolean).join(', '),
                }))}
                valeur={(l) => l.n}
                vide="Aucune tâche ouverte assignée."
              />
            </section>
            <section className="carte">
              <h2 className="carte-titre">Projets actifs par concession ou client</h2>
              <Rang
                lignes={d.entreprises.map((e) => ({ libelle: e.nom, n: e.projets }))}
                valeur={(l) => l.n}
                couleur="var(--serie-3)"
                vide="Aucun projet actif."
              />
            </section>
          </div>

          <section className="carte">
            <h2 className="carte-titre">Activité récente</h2>
            {d.activite.length === 0 ? <Vide>Pas encore d’activité.</Vide> : (
              <div className="liste-simple">
                {d.activite.map((a) => (
                  <Link key={a.id} className="liste-ligne" to={`/projets/${a.projet_id}?tache=${a.tache_id}`}>
                    <span className="liste-titre" style={{ whiteSpace: 'normal' }}>
                      <b>{nomComplet(a, 'auteur_') || 'Profil supprimé'}</b>{' '}
                      <span style={{ fontWeight: 400 }}>{a.type === 'message' ? `a écrit : « ${a.texte.length > 120 ? `${a.texte.slice(0, 120)}…` : a.texte} »` : a.texte}</span>
                      <div className="liste-sous">{a.tache_titre}</div>
                    </span>
                    <span className="faible" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{ilYA(a.created_at)}</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
